/** One workspace: who is in it, with which role, and the way to add more. */

import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { Avatar, panelClass } from "@/components/display";
import { InlineSelect, Select, TextInput } from "@/components/fields";
import { Empty, LoadError, Loading, useReportError } from "@/components/states";
import type { WsRole } from "@/features/session/types";
import { useAddMember, useDeleteWorkspace, useMembers, useRemoveMember, useSetMemberRole } from "./hooks";
import type { Workspace } from "./types";

export function MembersPanel({ workspace }: { workspace: Workspace }) {
  const { data: members, isPending, isError, error } = useMembers(workspace.wsid);
  const add = useAddMember(workspace.wsid);
  const setRole = useSetMemberRole(workspace.wsid);
  const remove = useRemoveMember(workspace.wsid);
  const destroy = useDeleteWorkspace();
  const reportError = useReportError();
  const [email, setEmail] = useState("");
  const [role, setNewRole] = useState<WsRole>("member");
  const [deleting, setDeleting] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await add.mutateAsync({ email, role });
      setEmail("");
    } catch (err) {
      reportError(err);
    }
  }

  return (
    <section aria-label={`Members of ${workspace.name}`} className={panelClass}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
        <div>
          <h2 className="font-semibold text-zinc-950 dark:text-white">{workspace.name}</h2>
          <p className="text-xs text-zinc-500">
            Created {new Date(workspace.created_at).toLocaleDateString()} · {members?.length ?? "…"} members
          </p>
        </div>
        <Button tone="ghost" onClick={() => setDeleting(true)} className="hover:text-red-600 dark:hover:text-red-400">
          Delete workspace
        </Button>
      </header>

      <form onSubmit={submit} className="grid gap-2 border-b border-zinc-100 bg-zinc-50/60 px-5 py-3 sm:grid-cols-[1fr_8rem_auto] dark:border-zinc-800 dark:bg-zinc-900/60">
        <TextInput value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add by email — name@qure.ai"
          aria-label="Member email" type="email" required />
        <Select value={role} onChange={(e) => setNewRole(e.target.value as WsRole)} aria-label="Role for new member">
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </Select>
        <Button type="submit" disabled={add.isPending}>Add member</Button>
      </form>

      {isPending ? (
        <Loading what="members" />
      ) : isError ? (
        <LoadError what="members" error={error} />
      ) : members.length === 0 ? (
        <Empty title="No members yet">Add people by email above. They don't need to have signed in before.</Empty>
      ) : (
        <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {members.map((m) => (
            <li key={m.sub} className="flex items-center gap-3 px-5 py-2.5">
              <Avatar name={m.name} email={m.email} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{m.name || m.email.split("@")[0]}</p>
                <p className="truncate text-xs text-zinc-500">{m.email}</p>
              </div>
              <InlineSelect value={m.role} aria-label={`Role for ${m.email}`}
                onChange={(e) => setRole.mutate({ sub: m.sub, role: e.target.value as WsRole }, { onError: reportError })}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </InlineSelect>
              <Button tone="ghost" aria-label={`Remove ${m.email}`} onClick={() => remove.mutate(m.sub, { onError: reportError })}
                className="hover:text-red-600 max-sm:w-9 max-sm:px-0 dark:hover:text-red-400">
                <X size={16} className="sm:hidden" />
                <span className="max-sm:sr-only">Remove</span>
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title={`Delete ${workspace.name}?`}
        description="Members lose access to it in every product. The workspace is kept in the database and can be restored there."
        footer={
          <>
            <Button onClick={() => setDeleting(false)}>Keep it</Button>
            <Button tone="danger" onClick={() => {
              destroy.mutate(workspace.wsid, { onError: reportError });
              setDeleting(false);
            }}>
              Delete workspace
            </Button>
          </>
        }
      />
    </section>
  );
}
